import React from "react";
import { Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { sanitizeRichHtml } from "@/lib/richHtml";
import Linkify from "linkify-react";

// An @handle not preceded by a word char, '@' or '.', so emails (jane.doe@x.com) and
// "@@" are never treated as mentions. Charset matches valid handle characters.
const RE_SRC = "(^|[^\\w@.])@([A-Za-z0-9][A-Za-z0-9-]{0,39})";
// Same pattern, re-exported for the in-composer live highlighters (TipTap
// decoration + textarea backdrop) so they match mentions identically.
export const MENTION_RE_SRC = RE_SRC;

// Linkify options used for comment bodies. We only autolink real URLs — emails
// are explicitly disabled so they can't collide with the @mention pass that
// runs first inside renderTextWithMentions.
const LINKIFY_OPTS = {
  defaultProtocol: "https",
  target: "_blank",
  rel: "noopener noreferrer nofollow",
  className: "so-link",
  attributes: { "data-testid": "comment-link" },
  validate: { email: false },
};

export function mentionMap(mentions) {
  const map = {};
  (mentions || []).forEach((m) => {
    if (m && m.handle_norm) map[m.handle_norm] = m;
  });
  return map;
}

const mentionHref = (entry) => `/@${entry.handle || entry.handle_norm}`;

/** Plain text → array of React nodes, valid mentions become display-name links,
 * and any URLs in the remaining text are auto-wrapped in <a> tags via linkify. */
export function renderTextWithMentions(text, mentions) {
  if (!text) return text;
  const map = mentionMap(mentions);
  // Fast path: no mentions in this comment — let linkify handle the whole string.
  if (!Object.keys(map).length) {
    return <Linkify options={LINKIFY_OPTS}>{text}</Linkify>;
  }
  const re = new RegExp(RE_SRC, "g");
  const nodes = [];
  let last = 0;
  let key = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    const [full, pre, handle] = m;
    const entry = map[handle.toLowerCase()];
    nodes.push(text.slice(last, m.index) + pre);
    if (entry) {
      nodes.push(
        <Link key={`m${key++}`} to={mentionHref(entry)} className="so-mention" data-testid="mention-link">
          {entry.display_name}
        </Link>
      );
    } else {
      nodes.push(`@${handle}`);
    }
    last = m.index + full.length;
  }
  nodes.push(text.slice(last));
  // Linkify walks string children and converts URLs to <a> tags; the mention
  // <Link> elements are React components so linkify leaves them alone.
  return <Linkify options={LINKIFY_OPTS}>{nodes}</Linkify>;
}

/** Sanitized chatter HTML → HTML string with valid @handles linkified to display names.
 * Walks text nodes only (never inside an existing <a>) and injects safe internal anchors. */
export function linkifyMentionsHtml(html, mentions) {
  const clean = sanitizeRichHtml(html || "");
  const map = mentionMap(mentions);
  if (!Object.keys(map).length) return clean;
  const doc = new DOMParser().parseFromString(`<div id="__root">${clean}</div>`, "text/html");
  const root = doc.getElementById("__root");
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
  const targets = [];
  let node;
  while ((node = walker.nextNode())) {
    if (node.parentElement && node.parentElement.closest("a")) continue;
    if (new RegExp(RE_SRC).test(node.nodeValue)) targets.push(node);
  }
  targets.forEach((tn) => {
    const text = tn.nodeValue;
    const frag = doc.createDocumentFragment();
    const re = new RegExp(RE_SRC, "g");
    let last = 0;
    let m;
    while ((m = re.exec(text)) !== null) {
      const [full, pre, handle] = m;
      const entry = map[handle.toLowerCase()];
      frag.appendChild(doc.createTextNode(text.slice(last, m.index) + pre));
      if (entry) {
        const a = doc.createElement("a");
        a.setAttribute("href", mentionHref(entry));
        a.setAttribute("class", "so-mention");
        a.setAttribute("data-mention", "1");
        a.textContent = entry.display_name;
        frag.appendChild(a);
      } else {
        frag.appendChild(doc.createTextNode(`@${handle}`));
      }
      last = m.index + full.length;
    }
    frag.appendChild(doc.createTextNode(text.slice(last)));
    tn.parentNode.replaceChild(frag, tn);
  });
  return root.innerHTML;
}

/** Find the @token being typed immediately before `caret` in `value`.
 * Returns { query, start } or null. Used by the autocomplete adapters. */
export function activeMentionToken(value, caret) {
  const before = value.slice(0, caret);
  const m = before.match(/(^|[^\w@.])@([A-Za-z0-9-]*)$/);
  if (!m) return null;
  return { query: m[2], start: caret - m[2].length - 1 };
}
